import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import toast from "react-hot-toast";
import { useTheme } from "../context/ThemeContext";
import useTenantFromSubdomain, { buildTenantUrl } from "../Hooks/useTenantFromSubdomain";
import { persistSessionTokens } from "../utils/session";

// Standalone tenant-selection page for an already-logged-in session that has
// no currentTenantId yet (e.g. a token issued before this field existed).
// Unlike TenantSelector.js (used mid-signin with a short-lived tempToken),
// this re-derives the tenant list from GET /api/auth/my-tenants using the
// caller's existing valid token, then switches via the normal
// POST /api/auth/switch-tenant endpoint — no re-authentication required.
const SelectTenantPage = () => {
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [switchingId, setSwitchingId] = useState(null);
  const navigate = useNavigate();
  const themeContext = useTheme();
  const tenantSubdomain = useTenantFromSubdomain();

  useEffect(() => {
    client
      .get("/auth/my-tenants")
      .then((res) => setTenants(res.data.availableTenants || []))
      .catch(() => toast.error("Failed to load your tenants."))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!isLoading && tenants.length === 1) {
      handleSelect(tenants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, tenants]);

  const handleSelect = async (tenant) => {
    setSwitchingId(tenant.id);
    try {
      const res = await client.post("/auth/switch-tenant", { tenantId: tenant.id });
      const data = res.data;
      const targetSubdomain = data.tenant?.subdomain;

      if (targetSubdomain && targetSubdomain !== tenantSubdomain) {
        const params = new URLSearchParams({
          token: data.token,
          refreshToken: data.refreshToken || "",
          email: data.email || "",
          roles: data.roles || "",
          userId: data.id || "",
          hubspotUserId: data.hubspot_user_id || "",
          activeTenantId: data.activeTenantId ?? "",
          isSuperAdmin: data.isSuperAdmin ? "true" : "false",
        });
        window.location.replace(`${buildTenantUrl(targetSubdomain, "/auth/cross-login")}#${params.toString()}`);
        return;
      }

      persistSessionTokens(data);
      themeContext?.fetchTheme();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to select tenant.");
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <div className="bg-canvas-primary h-screen">
      <div className="mainContainer flex flex-col justify-center items-center w-full h-full py-12 lg:py-24">
        <div className="bg-canvas-white py-11 px-5 flex gap-3 flex-col items-center rounded-3xl w-[350px]">
          <h1 className="text-canvas-primary font-bold text-center">Select account</h1>
          {isLoading && <p className="text-canvas-secondery text-[11.25px]">Loading...</p>}
          {!isLoading && tenants.length === 0 && (
            <p className="text-canvas-secondery text-[11.25px]">No tenants found for your account.</p>
          )}
          {!isLoading &&
            tenants.map((tenant) => (
              <button
                key={tenant.id}
                type="button"
                disabled={switchingId !== null}
                onClick={() => handleSelect(tenant)}
                className="w-full flex items-center gap-3 justify-center text-center font-outfit text-[12px] uppercase font-bold bg-canvas-primary text-canvas-white py-3 px-5 rounded-[37px] disabled:cursor-not-allowed disabled:bg-canvas-primary/20 disabled:text-canvas-primary"
              >
                {tenant.name}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default SelectTenantPage;
